import { Link, Navigate } from "react-router-dom";
import { UserPlus, User, Mail, Calendar, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import { useRegisterContentCreator } from "../hooks/useRegisterContentCreator";
import SpecializationStep from "../components/SpecializationStep";
import InputField from "../../../shared/components/form/InputField";
import PhoneField from "../../../shared/components/form/PhoneField";
import PasswordField from "../components/PasswordField";
import GalleryUploadStep from "../components/GalleryUploadStep";
import { useLanguage } from "../../../context/LanguageContext";

// eslint-disable-next-line react-hooks/rules-of-hooks
export default function RegisterContentCreator() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const STEPS = [
    t("auth.registerTalent.stepBasicInfo"),
    t("auth.registerContentCreator.stepSpecializations"),
    t("auth.registerTalent.stepGallery"),
  ];
  if (user) return <Navigate to="/" replace />;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    step,
    setStep,
    form,
    setField,
    markFieldTouched,
    errors,
    loading,
    phoneExists,
    checkingPhone,
    checkingEmail,
    isStep1Valid,
    toggleContentCreatorType,
    contentCreatorTypes,
    typesLoading,
    handleStep1,
    handleSubmit,
    profileImage,
    setProfileImage,
    gallery,
    setGallery,
    handleGallerySubmit,
  } = useRegisterContentCreator();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setField(e.target.name as keyof typeof form, e.target.value);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    markFieldTouched(e.target.name as keyof typeof form);

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-white via-gray-50 to-white flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#b3d130] rounded-full mb-4 shadow-md">
              <UserPlus size={32} className="text-[#313131]" />
            </div>
            <h1 className="text-3xl font-bold text-[#313131] mb-2">{t("auth.registerContentCreator.title")}</h1>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-center mb-8 gap-0">
            {STEPS.map((label, idx) => {
              const stepNum = idx + 1;
              const done = step > stepNum;
              const active = step === stepNum;
              return (
                <div key={stepNum} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                      done ? "bg-[#b3d130] border-[#b3d130] text-gray-900"
                        : active ? "bg-white border-[#b3d130] text-[#b3d130]"
                        : "bg-white border-gray-300 text-gray-400"
                    }`}>
                      {done ? <Check size={16} /> : stepNum}
                    </div>
                    <span className={`text-xs mt-1 font-medium ${active ? "text-[#b3d130]" : done ? "text-gray-600" : "text-gray-400"}`}>
                      {label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`w-16 h-0.5 mb-4 mx-1 transition-all ${step > stepNum ? "bg-[#b3d130]" : "bg-gray-200"}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Steps */}
          {step === 1 && (
            <motion.form initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" onSubmit={handleStep1}>
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 mb-1">{t("auth.basicInfoStep.title")}</h3>
                <p className="text-sm text-gray-500">{t("auth.basicInfoStep.subtitle")}</p>
              </div>
              <InputField label={t("auth.firstName")} icon={User} required name="first_name" value={form.first_name} onChange={onChange} onBlur={onBlur} error={errors.first_name} placeholder={t("auth.registerContentCreator.firstNamePlaceholder")} />
              <InputField label={t("auth.lastName")} icon={User} required name="last_name" value={form.last_name} onChange={onChange} onBlur={onBlur} error={errors.last_name} placeholder={t("auth.registerContentCreator.lastNamePlaceholder")} />
              <InputField label={t("auth.emailAddress")} icon={Mail} required type="email" name="email" value={form.email} onChange={onChange} onBlur={onBlur} error={errors.email} placeholder={t("auth.emailPlaceholder")} />
              <PhoneField required value={form.phone} onChange={(v) => setField("phone", v)} onCountryChange={(c) => setField("phone_country", c ?? "")} onBlur={() => markFieldTouched("phone")} error={errors.phone} phoneExists={phoneExists} checkingPhone={checkingPhone} />
              <InputField label={t("auth.dateOfBirth")} icon={Calendar} required type="date" name="date_of_birth" value={form.date_of_birth} onChange={onChange} onBlur={onBlur} error={errors.date_of_birth} placeholder="" />
              <PasswordField label={t("auth.password")} name="password" value={form.password} onChange={onChange} onBlur={onBlur} error={errors.password} placeholder={t("auth.passwordPlaceholder")} />
              <PasswordField label={t("auth.confirmPassword")} name="confirmPassword" value={form.confirmPassword} onChange={onChange} onBlur={onBlur} error={errors.confirmPassword} placeholder={t("auth.confirmPasswordPlaceholder")} />
              <button type="submit" disabled={!isStep1Valid}
                className="w-full bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed">
                {checkingPhone || checkingEmail ? t("auth.registerContentCreator.validating") : t("auth.registerContentCreator.continueToSpecializations")}
              </button>
            </motion.form>
          )}

          {step === 2 && (
            <SpecializationStep
              selectedContentTypes={form.content_creator_types}
              onToggleContentType={toggleContentCreatorType}
              contentTypes={contentCreatorTypes}
              typesLoading={typesLoading}
              onBack={() => setStep(1)}
              onSubmit={handleSubmit}
              loading={false}
            />
          )}

          {step === 3 && (
            <GalleryUploadStep
              gallery={gallery}
              profileImage={profileImage}
              isLoading={loading}
              onGalleryChange={setGallery}
              onProfileImageChange={setProfileImage}
              onBack={() => setStep(2)}
              onSubmit={handleGallerySubmit}
            />
          )}

          <div className="mt-6 text-center text-sm text-gray-600">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-[#b3d130] font-semibold hover:underline">{t("auth.signInLink")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
